// ════════════════════════════════════════════════════════════
//  online/guests.js — GUESTS IN THE CIRCUIT (caP'n 2026-10-20)
// ════════════════════════════════════════════════════════════
//  "in the 10 minutes prior to the tourney, anyone can join and replace the lowest fighters, then the
//  tourney let's you join proper <3 if a player/partner wins they get KK 1 for now is fine".
//
//  · The SEATS are written with the hour's lock at :50 (circuit.js guestSeats): the bottom of the
//    Circuit's table, each in their first league bout of the hour.
//  · ONE ANSWER FOR EVERY SCREEN: at the hour the first screen whose clock the server has confirmed
//    writes seatsFinal (a transaction, once) — the claims as they stood. Every screen fights exactly
//    that; until it exists, a seat's bout waits (no spec, no bets). And once a bout's spec is written,
//    the spec is the authority on who fights it (stage.js).
//  · A claim's team must be three real beings (and its Tomes real Tomes), or it never counts —
//    a stale or forged team can't freeze the League.
//  · From :50 to the top of the hour anyone signed in takes a seat with their own three — first come,
//    one seat each (a transaction per seat). The claim carries the SERVER's time; one made at or after
//    the hour (a slow clock, a late tab) is ignored by every screen, so every screen agrees who fights.
//  · At the top of the hour the seats close. A taken seat puts the guest in that bout in the NPC's
//    place (stage.js); an empty one stays the NPC's. A guest who doesn't show: their partner (their
//    Mark's partner) fights for them — the AI takeover — or, with no partner, a forfeit (the lockstep
//    no-show rule).
//  · A guest's bout is an exhibition: no ledger row (rows.js), so the table, the ratings and the minds
//    never see it. Bets on it open when the seats close (a bet's pool is per pairing).
//  · A win pays GUEST_PURSE King's Krests, collected by the guest's own screen (it only ever writes
//    its own wallet — the Lane Law), grant first, flag second.
// ════════════════════════════════════════════════════════════
import { BASE } from "./ie.js";
import { hourPath } from "./ledger.js";
import { hourStart, lockAtOf, guestKey, GUEST_PURSE } from "./circuit.js";
import { boutPath } from "./stage.js";
import { LINES } from "../data/roster.js";
import { TOME_IDS } from "../data/arts.js";

export const SEAT_GRACE_MS = 2000;                  // the seats are frozen this long past the hour (the specs come at :00:03)
export const seatsPath = (h) => `${hourPath(h)}/seats`;
export const finalPath = (h) => `${hourPath(h)}/seatsFinal`;
export const minePath = (uid) => `${BASE}/league/guests/${uid}`;   // my seats, for the King's Krests collector

const finalCache = new WeakMap();                    // net → Map(h → final claims): frozen, never changes
const cacheOf = (net) => { let m = finalCache.get(net); if (!m) { m = new Map(); finalCache.set(net, m); } return m; };

/** Is hour h taking seats right now? (the card is up and the hour hasn't started) */
export const seatsOpen = (h, now) => now >= lockAtOf(h) && now < hourStart(h);

export const validTeam = (t) => Array.isArray(t) && t.length === 3 && new Set(t).size === 3 && t.every((x) => typeof x === "string" && LINES[x]);
const cleanTomes = (tm) => (Array.isArray(tm) && tm.length && tm.every((x) => TOME_IDS.includes(x)) ? tm.slice(0, 3) : null);
const vacated = (c, h) => typeof c.left === "number" && c.left < hourStart(h);
/** A claim that counts: a uid, a real three, stamped by the server before the hour, not given back before it. */
const validClaim = (c, h) => !!c && typeof c.u === "string" && validTeam(c.t) && typeof c.at === "number" && c.at < hourStart(h) && !vacated(c, h);
const asGuest = (c) => ({ uid: c.u, name: String(c.n || "A guest").slice(0, 24), team: c.t.slice(), tomes: cleanTomes(c.tm), partner: !!c.p });

/** The frozen answer: { i: guest } — written once, by whoever gets there first. */
async function freeze(net, h) {
  const raw = (await net.get(seatsPath(h)).catch(() => null)) || {};
  const snap = { at: hourStart(h) };
  for (const [i, c] of Object.entries(raw)) if (validClaim(c, h)) snap[i] = asGuest(c);
  // ⚠ Firebase runs a transaction first on its (often empty) local copy: null → our snapshot; the server re-runs it on the real one
  const r = await net.txn(finalPath(h), (cur) => (cur == null ? snap : undefined)).catch(() => null);
  return (r && r.value) || (await net.get(finalPath(h)).catch(() => null));
}

/**
 * The hour's claims: { final, byIndex: { i: guest } }  guest = { uid, name, team, tomes, partner, pending? }.
 * final = frozen (every screen fights exactly this). Before that: what's claimed so far (for the strip).
 */
export async function seatClaims(net, h, now) {
  const cache = cacheOf(net);
  if (cache.has(h)) return cache.get(h);
  let fin = await net.get(finalPath(h)).catch(() => null);
  const clockOk = !net.clockKnown || net.clockKnown();
  if (!fin && now >= hourStart(h) + SEAT_GRACE_MS && clockOk) fin = await freeze(net, h);
  if (fin) {
    const byIndex = {};
    for (const [i, g] of Object.entries(fin)) if (i !== "at" && g && typeof g.uid === "string" && validTeam(g.team)) byIndex[i] = { ...g, tomes: cleanTomes(g.tomes) };
    const v = { final: true, closed: true, byIndex };
    cache.set(h, v);
    return v;
  }
  const raw = (await net.get(seatsPath(h)).catch(() => null)) || {};
  const byIndex = {};
  for (const [i, c] of Object.entries(raw)) {
    if (!c || typeof c.u !== "string" || !validTeam(c.t) || vacated(c, h)) continue;
    byIndex[i] = { ...asGuest(c), pending: !validClaim(c, h) };
  }
  return { final: false, closed: false, byIndex };
}

/** A guest as an entrant (stage.js puts it in the NPC's place). */
export const guestEntrant = (g) => ({ key: guestKey(g.uid), name: g.name, team: g.team.slice(), tomes: g.tomes || null, home: "guest", uid: g.uid, partner: !!g.partner, style: null, change: null });

const isFree = (c, h) => !c || vacated(c, h);

/**
 * Take a seat in hour h. seats = the lock's seats. Resolves { ok, i } or { ok:false, reason } (never throws).
 */
export async function takeSeat(net, { h, seats, uid, name, team, tomes = null, partner = false, now }) {
  if (!uid) return { ok: false, reason: "Sign in to take a seat." };
  if (!validTeam(team)) return { ok: false, reason: "Pick your three first." };
  if (!seatsOpen(h, now)) return { ok: false, reason: "Seats are only open in the ten minutes before the hour." };
  if (!Array.isArray(seats) || !seats.length) return { ok: false, reason: "No seats this hour." };
  const raw = (await net.get(seatsPath(h)).catch(() => null)) || {};
  if (Object.values(raw).some((c) => c && c.u === uid && !vacated(c, h))) return { ok: false, reason: "You already have a seat this hour." };
  const claim = { u: uid, n: String(name || "A guest").slice(0, 24), t: team.slice(0, 3), ...(cleanTomes(tomes) ? { tm: cleanTomes(tomes) } : {}), p: partner ? 1 : 0 };
  for (let i = 0; i < seats.length; i++) {
    if (!seats[i] || !isFree(raw[i], h)) continue;
    const r = await net.txn(`${seatsPath(h)}/${i}`, (cur) => (isFree(cur, h) ? claim : undefined)).catch(() => null);
    if (!r || !r.committed) continue;
    // the SERVER stamps when it was taken (a transaction can't carry the server's clock): no stamp → it never counts
    const stamped = await net.set(`${seatsPath(h)}/${i}/at`, net.TS ? net.TS() : now).then(() => true).catch(() => false);
    if (!stamped) { await net.set(`${seatsPath(h)}/${i}`, null).catch(() => {}); return { ok: false, reason: "Couldn't take the seat — try again." }; }
    // two tabs at once can each win a seat: keep the lowest, give the rest back
    const after = (await net.get(seatsPath(h)).catch(() => null)) || {};
    const mine = Object.keys(after).filter((k) => after[k] && after[k].u === uid && !vacated(after[k], h)).map(Number).sort((a, b) => a - b);
    for (const k of mine.slice(1)) await net.set(`${seatsPath(h)}/${k}/left`, net.TS ? net.TS() : now).catch(() => {});
    const keep = mine.length ? mine[0] : i, s = seats[keep];
    await net.set(`${minePath(uid)}/${h}`, { i: keep, j: s.j, side: s.side }).catch(() => {});
    return { ok: true, i: keep };
  }
  return { ok: false, reason: "All the seats are taken this hour." };
}

/** Give a seat back (only before the hour — the server's stamp decides, so a slow clock can't take a seat away late). */
export async function leaveSeat(net, { h, uid, now }) {
  if (!seatsOpen(h, now)) return { ok: false, reason: "The seats have closed." };
  const raw = (await net.get(seatsPath(h)).catch(() => null)) || {};
  const i = Object.keys(raw).find((k) => raw[k] && raw[k].u === uid && !vacated(raw[k], h));
  if (i == null) return { ok: false, reason: "You don't have a seat." };
  await net.set(`${seatsPath(h)}/${i}/left`, net.TS ? net.TS() : now).catch(() => {});
  await net.set(`${minePath(uid)}/${h}`, null).catch(() => {});
  return { ok: true };
}

/**
 * THE GUEST'S PURSE: for each of my past seats with a result, pay my win (King's Krests) — ONCE, even with
 * two tabs open: a seat is claimed for paying by a transaction first, then paid, then marked.
 * pay(amount) → the new balance or null (refused). Resolves the total paid this pass.
 */
export async function collectGuestPay(net, uid, now, pay, { maxAgeH = 48 } = {}) {
  if (!uid) return 0;
  const mine = (await net.get(minePath(uid)).catch(() => null)) || {};
  let paid = 0;
  for (const [hs, s] of Object.entries(mine)) {
    const h = +hs, p = `${minePath(uid)}/${hs}`;
    if (!s || !Number.isFinite(h) || now < hourStart(h)) continue;
    const old = now - hourStart(h) > maxAgeH * 3600e3;
    if (s.paid != null) { if (old) await net.set(p, null).catch(() => {}); continue; }   // settled and old: tidy it away
    if (old) { await net.set(`${p}/paid`, "old").catch(() => {}); continue; }
    const claims = await seatClaims(net, h, now);
    if (!claims.final) continue;
    const g = claims.byIndex[s.i];
    if (!g || g.uid !== uid) { await net.set(`${p}/paid`, "nobout").catch(() => {}); continue; }   // the claim didn't count
    const spec = await net.get(`${boutPath(h, s.j)}/spec`).catch(() => null);
    if (spec && spec.void) { await net.set(`${p}/paid`, "void").catch(() => {}); continue; }
    const res = await net.get(`${boutPath(h, s.j)}/result`).catch(() => null);
    if (!res || (res.winner !== 0 && res.winner !== 1)) continue;   // not decided yet: next pass
    if (res.winner !== s.side) { await net.set(`${p}/paid`, 0).catch(() => {}); continue; }
    // claim it for paying (one tab wins), pay, mark. A crash between pay and mark leaves it claimed: never paid twice.
    const c = await net.txn(`${p}/paying`, (cur) => (cur == null ? now : undefined)).catch(() => null);
    if (!c || !c.committed) continue;
    const after = await pay(GUEST_PURSE);
    if (after == null) { await net.set(`${p}/paying`, null).catch(() => {}); continue; }   // refused: let the next pass try
    paid += GUEST_PURSE;
    await net.set(`${p}/paid`, GUEST_PURSE).catch(() => {});
  }
  return paid;
}
